// src/theme/tokens.js
// MIHU Design Tokens — single source of truth for the brand system.
// Consumed by MihuThemeProvider to inject CSS custom properties at runtime.

export const tokens = {
  colors: {
    black: "#000000",
    navy: "#2F3A78",        // Dark Blue — Primary
    orange: "#D16A4B",      // Burnt Orange — Secondary
    teal: "#2A8F85",        // Matte Teal — Accent
    gold: "#D9A441",        // Curry Gold — Achievement
    offwhite: "#F5EFE7",    // Off-White — Background
    slate: "#5C5756",       // Slate Gray — Muted text
    charcoal: "#2F2F2F",    // Charcoal Ink — Body text

    // Pillar colors
    pillarMind: "#4A5568",
    pillarBody: "#D16A4B",
    pillarCommunity: "#2A8F85",
    pillarSpirit: "#D9A441",
  },

  fonts: {
    heading: "'Playfair Display', Georgia, serif",
    body: "'Montserrat', ui-sans-serif, system-ui, sans-serif",
    accent: "'Raleway', ui-sans-serif, sans-serif",
  },

  // Typography scale (rem)
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
    "8xl": "6rem",
  },

  letterSpacing: {
    tighter: "-0.02em",
    wide: "0.1em",
    widest: "0.2em",
  },

  lineHeight: {
    tight: "1.1",
    relaxed: "1.6",
    loose: "1.8",
  },

  radius: {
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    "2xl": "1.5rem",
    full: "9999px",
  },

  shadows: {
    sm: "0 1px 2px rgba(0,0,0,0.05)",
    md: "0 4px 12px rgba(0,0,0,0.08)",
    lg: "0 12px 32px rgba(0,0,0,0.12)",
  },

  transitions: {
    fast: "150ms cubic-bezier(0.4,0,0.2,1)",
    base: "300ms cubic-bezier(0.22,1,0.36,1)",
    slow: "500ms cubic-bezier(0.22,1,0.36,1)",
  },

  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },
};

// Flat map of CSS variable name -> value, injected by the provider.
//
// NOTE: the font entries are prefixed --mihu-font-* rather than
// --font-heading/--font-body/--font-accent. The existing app CSS
// (AlbumCard.css, EventCard.css, LandingPage.css, etc.) already uses
// --font-body, --font-caption, --font-h3, --font-label as FONT-SIZE
// variables (e.g. `font-size: var(--font-body, 14px)`). If this map set
// --font-body to a font-family string instead, it would silently break
// every font-size declaration using that name the moment the provider
// mounts. Keeping these distinct avoids that collision entirely.
export const cssVarMap = {
  "--mihu-black": tokens.colors.black,
  "--mihu-navy": tokens.colors.navy,
  "--mihu-orange": tokens.colors.orange,
  "--mihu-teal": tokens.colors.teal,
  "--mihu-gold": tokens.colors.gold,
  "--mihu-offwhite": tokens.colors.offwhite,
  "--mihu-slate": tokens.colors.slate,
  "--mihu-charcoal": tokens.colors.charcoal,
  "--mihu-pillar-mind": tokens.colors.pillarMind,
  "--mihu-pillar-body": tokens.colors.pillarBody,
  "--mihu-pillar-community": tokens.colors.pillarCommunity,
  "--mihu-pillar-spirit": tokens.colors.pillarSpirit,
  "--mihu-font-heading": tokens.fonts.heading,
  "--mihu-font-body": tokens.fonts.body,
  "--mihu-font-accent": tokens.fonts.accent,
  "--mihu-radius": tokens.radius.lg,
};

export default tokens;